/**
 * Presentation rules for tournaments. Pure — no React, no fetching — so the
 * fiddly bits are unit-tested, like lib/slots.ts and lib/admin-calendar.ts.
 */

import type { StandingRead } from '../api/generated/model'

/** The four states a tournament moves through, as a person would say them. */
export const TOURNAMENT_STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  open: 'Inscripciones abiertas',
  closed: 'Inscripciones cerradas',
  completed: 'Finalizado',
}

/**
 * How a position reads on a scoreboard.
 *
 * Golf writes a shared position with a T: two players on the same net are
 * "T2", and a lone leader is just "1". A blank means they have not played yet
 * — NOT last place, which is what a dash or a zero would imply while an event
 * is still running.
 */
export function positionLabel(standings: readonly StandingRead[], index: number): string {
  const row = standings[index]
  if (row?.position == null) return ''
  const shared = standings.filter((other) => other.position === row.position).length > 1
  return shared ? `T${row.position}` : String(row.position)
}

/** A signed handicap reads better with its sign: "+2" off, "0" scratch. */
export const handicapLabel = (handicap: number): string =>
  handicap > 0 ? `+${handicap}` : String(handicap)

/**
 * Whether entries can be taken right now, from the customer's point of view.
 * A full tournament is open but not enterable, and the difference is what the
 * button has to say.
 */
export function entryState(tournament: {
  status: string
  spots_left: number | null
}): 'open' | 'full' | 'closed' {
  if (tournament.status !== 'open') return 'closed'
  if (tournament.spots_left !== null && tournament.spots_left <= 0) return 'full'
  return 'open'
}

/**
 * When a tournament happens, as a person would say it.
 *
 * A single `event_date` is a lie for a tournament you may play across a week:
 * the card said "viernes, 11 de setiembre" for an event open from the 6th to
 * the 16th. When the playing period is one day the two agree and this reads
 * exactly as it did before.
 */
export function whenLabel(
  eventDate: string,
  playsFrom: string,
  playsUntil: string,
  long: (date: string) => string,
): string {
  if (playsFrom === playsUntil) return long(eventDate)
  return `Del ${long(playsFrom)} al ${long(playsUntil)}`
}
